<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import { cloneValue } from '~/utils/cloneValue';

const emits = defineEmits(['submit-question']);

const catalogStore = useCatalogStore();
const feedbackStore = useFeedbackStore();
const { userDetails } = storeToRefs(useProfileStore());

const initialQuestionFormState = {
  model: 'product',
  model_id: catalogStore.selectedOffer.product_id,
  message: '',
  person_name: userDetails.value ? `${userDetails.value.first_name} ${userDetails.value.second_name.charAt(0)}.` : '',
  images: [] as File[],
};

const questionForm = ref(cloneValue(initialQuestionFormState));
const errors = ref({ name: '', message: '' });
const isLoading = ref(false);
const { isMobile } = useBreakpoints();
const previews = ref<string[]>([]);

const messageMinLength = ref(10);
const messageMaxLength = ref(3000);
const nameMinLength = ref(2);

watch(
  () => questionForm.value,
  () => {
    if (questionForm.value.person_name && questionForm.value.person_name.length > nameMinLength.value)
      errors.value.name = '';

    if (
      questionForm.value.message &&
      questionForm.value.message.length >= messageMinLength.value &&
      questionForm.value.message.length <= messageMaxLength.value
    )
      errors.value.message = '';
  },
  { deep: true },
);

const hasErrors = computed(() => !!errors.value.name || !!errors.value.message);

const yandexCaptcha = ref();
const captchaToken = ref('');

watch(
  () => captchaToken.value,
  () => {
    if (captchaToken.value) submitQuestion();
  },
);

async function submitQuestion() {
  if (!questionForm.value.person_name) errors.value.name = 'Введите имя';
  if (questionForm.value.person_name.length < nameMinLength.value)
    errors.value.name = `Минимум ${nameMinLength.value} символов`;

  if (!questionForm.value.message) errors.value.message = 'Поле обязательно для заполнения';
  if (questionForm.value.message.length < messageMinLength.value)
    errors.value.message = `Минимум ${messageMinLength.value} символов`;

  if (hasErrors.value) return;

  isLoading.value = true;

  if (captchaToken.value) {
    const response = await feedbackStore.sendQuestion(questionForm.value, captchaToken.value);

    if (response?.success) {
      const toast = useToast();
      toast.success({
        component: ToastComponent,
        props: { text: 'Спасибо! Ваш вопрос отправлен и будет опубликован после проверки модератором.' },
        timeout: 6000,
      });

      questionForm.value = cloneValue(initialQuestionFormState);
      previews.value = [];
      emits('submit-question');
    }
    yandexCaptcha.value?.reset();
  } else {
    yandexCaptcha.value?.execute();
  }

  isLoading.value = false;
}
</script>

<template>
  <div class="add-question-form">
    <div class="flex flex-col gap-3 mt-4">
      <TextareaField
        v-model="questionForm.message"
        :autofocus="isMobile"
        :error="errors.message"
        :max-len="messageMaxLength"
        :min-len="messageMinLength"
        name="message"
        placeholder="Введите свой вопрос"
      />

      <InputField
        v-model="questionForm.person_name"
        :error="errors.name"
        :min-len="nameMinLength"
        name="person name"
        placeholder="Ваше имя"
      />

      <ProductPageFeedbackImagesUploadBlock
        v-model:images="questionForm.images"
        v-model:previews="previews"
      />
    </div>

    <TheBtn
      :disabled="hasErrors"
      :is-loading="isLoading"
      :size="isMobile ? 'sm' : 'lg'"
      class="w-full"
      color="outline"
      @click="submitQuestion"
    >
      Задать свой вопрос
    </TheBtn>

    <YandexCaptcha
      ref="yandexCaptcha"
      v-model="captchaToken"
    />
  </div>
</template>

<style lang="scss" scoped>
.add-question-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 580px;
}
</style>
